import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useIsHeavyReport } from 'hooks';
import {
    addStaticIndication,
    removeStaticIndication,
    STATIC_INDICATION_TYPES,
} from 'modules/taboola-common-frontend-modules/Indications';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import {
    AVAILABLE_BROWSER_OS_REPORT_START_DATE,
    AVAILABLE_MSV2_CREATIVE_REPORT_FILTERS_START_DATE,
    CATEGORY_REPORT_MISSING_DATA_END_DATE,
    CATEGORY_REPORT_MISSING_DATA_START_DATE,
    CONVERSION_DATA_EXISTS_FROM_DATE,
    getCategoryReportMissingDataIndication,
    getHeavyAccountReportFreshnessIndication,
    getPartialReportDataIndication,
    reportFiltersDataUnavailableIndication,
    historicalDataNotAvailableIndication,
    HISTORICAL_DATA_AVAILABLE_FROM_DATE,
    realtimeReportDisclaimer,
    reportHistoricalConversionsUnavailableIndication,
    REPORTS_WITH_TWO_YEARS_DATA,
    reportsWithPartialData,
    siteReportMSV2Disclaimer,
    twoYearsDataLimitIndication,
    UNAVAILABLE_MSV2_CREATIVE_REPORT_FILTERS,
    USE_WIDE_PERFORMANCE_TABLE_REPORTS,
    siteMSV2DynamicColumnsSortDisclaimer,
    RECOMMENDED_MSV2_SITE_DYNAMIC_SORT_FILTERS,
    byHourReportDataNotAvailableIndication,
} from '../config/indications/staticIndications';
import { ACCOUNT_REPORT_ID } from '../modules/campaigns-reports/config/reports/account/accountReportId';
import { BROWSER_REPORT_ID } from '../modules/campaigns-reports/config/reports/browser/browserReportId';
import { CAMPAIGN_REPORT_ID } from '../modules/campaigns-reports/config/reports/campaign/campaignReportId';
import { CAMPAIGNS_GROUPS_REPORT_ID } from '../modules/campaigns-reports/config/reports/campaigns-groups/campaignGroupReportId';
import { CATEGORY_REPORT_ID } from '../modules/campaigns-reports/config/reports/category/categoryReportId';
import { COUNTRY_REPORT_ID } from '../modules/campaigns-reports/config/reports/country/countryReportId';
import { CREATIVE_REPORT_ID } from '../modules/campaigns-reports/config/reports/creative/creativeReportId';
import { DAY_OF_WEEK_REPORT_ID } from '../modules/campaigns-reports/config/reports/day-of-week/dayWeekReportId';
import { DAY_REPORT_ID } from '../modules/campaigns-reports/config/reports/day/dayReportId';
import { HOUR_OF_DAY_REPORT_ID } from '../modules/campaigns-reports/config/reports/hour-of-day/hourDayReportId';
import { MONTH_REPORT_ID } from '../modules/campaigns-reports/config/reports/month/monthReportId';
import { OS_FAMILY_REPORT_ID } from '../modules/campaigns-reports/config/reports/os-family/osFamilyReportId';
import { OS_VERSION_REPORT_ID } from '../modules/campaigns-reports/config/reports/os-version/osVersionReportId';
import { PLATFORM_REPORT_ID } from '../modules/campaigns-reports/config/reports/platform/platformReportId';
import { REGION_REPORT_ID } from '../modules/campaigns-reports/config/reports/region/regionReportId';
import { SITE_REPORT_ID } from '../modules/campaigns-reports/config/reports/site/siteReportId';
import { WEEK_REPORT_ID } from '../modules/campaigns-reports/config/reports/week/weekReportId';
import { useReportFilters } from '../modules/campaigns-reports/hooks/useReportFilters';
import { useReportFiltersStatus } from '../modules/campaigns-reports/hooks/useReportFiltersStatus';
import { useReportSorting } from '../modules/campaigns-reports/hooks/useReportSorting';
import { useRealtime } from './useRealtime';

const WIDE_TABLE_REPORT_SET = new Set([
    DAY_REPORT_ID,
    WEEK_REPORT_ID,
    MONTH_REPORT_ID,
    DAY_OF_WEEK_REPORT_ID,
    CAMPAIGN_REPORT_ID,
    ACCOUNT_REPORT_ID,
    COUNTRY_REPORT_ID,
    REGION_REPORT_ID,
    PLATFORM_REPORT_ID,
    BROWSER_REPORT_ID,
    OS_FAMILY_REPORT_ID,
    OS_VERSION_REPORT_ID,
]);

const CHECK_DATA_NOT_AVAILABLE_REPORT_SET = new Set([
    ...WIDE_TABLE_REPORT_SET,
    CAMPAIGNS_GROUPS_REPORT_ID,
    CREATIVE_REPORT_ID,
    SITE_REPORT_ID,
]);

const useCampaignReportStaticIndications = ({ selectedReport, startDate, endDate, reportConfig }) => {
    const dispatch = useDispatch();
    const isHeavyReport = useIsHeavyReport();
    const { isRealtimeActive } = useRealtime();
    const { reportFilters } = useReportFilters();
    const [isFilterActive] = useReportFiltersStatus();
    const [reportSort] = useReportSorting(reportConfig);
    const isCategoryReportMissingDataIndicationEnabled = useConfigMatch({
        [FEATURE_FLAGS.SHOW_CATEGORY_REPORT_MISSING_DATA_INDICATION]: 'true',
    });
    const isWideConfigEnabledInCampaignReport = useConfigMatch({
        [FEATURE_FLAGS.CAMPAIGN_REPORT_USE_WIDE_CONFIG_MSV2]: 'true',
    });
    const isWideConfigEnabledInSiteReport = useConfigMatch({
        [FEATURE_FLAGS.SITE_REPORT_USE_WIDE_CONFIG_MSV2]: 'true',
    });
    const shouldStopUsingReportHistoricalData = useConfigMatch({
        [FEATURE_FLAGS.SHOULD_STOP_USING_REPORT_HISTORICAL_DATA]: 'true',
    });
    const removeIndication = useCallback(
        staticIndicationType => dispatch(removeStaticIndication({ staticIndicationType })),
        [dispatch]
    );
    const shouldShowSiteReportSortIndication = useMemo(() => {
        const hasNoReportFilters = !reportFilters || reportFilters.length === 0;
        const hasRecommendedFilter = reportFilters?.some(({ type }) =>
            RECOMMENDED_MSV2_SITE_DYNAMIC_SORT_FILTERS.includes(type)
        );
        const hasValidSort = reportSort?.length > 0 && reportSort.some(({ colId }) => !isNaN(colId.split('_').pop()));

        return (
            selectedReport === SITE_REPORT_ID &&
            isWideConfigEnabledInSiteReport &&
            (hasNoReportFilters || !hasRecommendedFilter) &&
            hasValidSort
        );
    }, [isWideConfigEnabledInSiteReport, reportFilters, reportSort, selectedReport]);

    // Browser report indication
    useEffect(() => {
        if (shouldStopUsingReportHistoricalData) {
            return;
        }
        const shouldShowIndication =
            reportsWithPartialData[selectedReport] &&
            moment(startDate).isBefore(AVAILABLE_BROWSER_OS_REPORT_START_DATE);

        if (!shouldShowIndication) {
            return;
        }

        dispatch(addStaticIndication(getPartialReportDataIndication()));
        return () => removeIndication(STATIC_INDICATION_TYPES.PARTIAL_REPORT_DATA);
    }, [dispatch, selectedReport, startDate, removeIndication, shouldStopUsingReportHistoricalData]);

    // Category missed data indications
    useEffect(() => {
        const shouldShowIndication =
            isCategoryReportMissingDataIndicationEnabled &&
            selectedReport === CATEGORY_REPORT_ID &&
            moment(startDate).isBefore(CATEGORY_REPORT_MISSING_DATA_END_DATE) &&
            moment(endDate).isAfter(CATEGORY_REPORT_MISSING_DATA_START_DATE);
        if (!shouldShowIndication) {
            return;
        }
        dispatch(addStaticIndication(getCategoryReportMissingDataIndication()));
        return () => removeIndication(STATIC_INDICATION_TYPES.CATEGORY_REPORT_MISSING_DATA);
    }, [dispatch, selectedReport, startDate, endDate, isCategoryReportMissingDataIndicationEnabled, removeIndication]);

    //Heavy account report freshness indication
    useEffect(() => {
        if (!isHeavyReport) {
            return;
        }

        dispatch(addStaticIndication(getHeavyAccountReportFreshnessIndication(selectedReport)));
        return () => removeIndication(STATIC_INDICATION_TYPES.HEAVY_ACCOUNT_REPORT_FRESHNESS);
    }, [selectedReport, isHeavyReport, dispatch, removeIndication]);

    // Realtime disclaimer
    useEffect(() => {
        if (!isRealtimeActive) {
            return;
        }

        dispatch(addStaticIndication(realtimeReportDisclaimer));
        return () => removeIndication(STATIC_INDICATION_TYPES.REALTIME_REPORT_DISCLAIMER);
    }, [isRealtimeActive, dispatch, removeIndication]);

    // Ads report new performance table data availability indication
    useEffect(() => {
        if (shouldStopUsingReportHistoricalData) {
            return;
        }
        const momentStartDate = moment(startDate);
        const dataUnavailableReasonStartPopulated = momentStartDate.isBefore(
            AVAILABLE_MSV2_CREATIVE_REPORT_FILTERS_START_DATE
        );
        const shouldShowIndication =
            (selectedReport === CREATIVE_REPORT_ID ||
                (isWideConfigEnabledInCampaignReport && WIDE_TABLE_REPORT_SET.has(selectedReport))) &&
            isFilterActive &&
            reportFilters &&
            reportFilters.length !== 0 &&
            reportFilters.some(({ type }) => UNAVAILABLE_MSV2_CREATIVE_REPORT_FILTERS.includes(type)) &&
            dataUnavailableReasonStartPopulated;

        if (!shouldShowIndication) {
            return;
        }

        dispatch(addStaticIndication(reportFiltersDataUnavailableIndication));
        return () => removeIndication(STATIC_INDICATION_TYPES.PARTIAL_REPORT_DATA);
    }, [
        dispatch,
        selectedReport,
        reportFilters,
        isFilterActive,
        startDate,
        removeIndication,
        isWideConfigEnabledInCampaignReport,
        shouldStopUsingReportHistoricalData,
    ]);

    // History conversions is not available
    useEffect(() => {
        if (shouldStopUsingReportHistoricalData) {
            return;
        }
        const shouldShowIndication =
            USE_WIDE_PERFORMANCE_TABLE_REPORTS.some(report => report === selectedReport) &&
            moment(startDate).isBefore(CONVERSION_DATA_EXISTS_FROM_DATE);
        if (!shouldShowIndication) {
            return;
        }
        dispatch(addStaticIndication(reportHistoricalConversionsUnavailableIndication));
        return () => removeIndication(STATIC_INDICATION_TYPES.PARTIAL_REPORT_DATA);
    }, [dispatch, selectedReport, startDate, removeIndication, shouldStopUsingReportHistoricalData]);

    // Site MSV2 Disclaimer
    useEffect(() => {
        if (shouldStopUsingReportHistoricalData) {
            return;
        }
        const shouldShowIndication =
            selectedReport === SITE_REPORT_ID &&
            isWideConfigEnabledInSiteReport &&
            moment(startDate).isBefore(AVAILABLE_MSV2_CREATIVE_REPORT_FILTERS_START_DATE);

        if (!shouldShowIndication) {
            return;
        }

        dispatch(addStaticIndication(siteReportMSV2Disclaimer));
        return () => removeIndication(STATIC_INDICATION_TYPES.SITE_MSV2_DISCLAIMER);
    }, [
        selectedReport,
        dispatch,
        removeIndication,
        isWideConfigEnabledInSiteReport,
        startDate,
        shouldStopUsingReportHistoricalData,
    ]);

    // Site MSV2 dynamic columns sort Disclaimer
    useEffect(() => {
        if (!shouldShowSiteReportSortIndication) {
            return;
        }

        dispatch(addStaticIndication(siteMSV2DynamicColumnsSortDisclaimer));
        return () => removeIndication(STATIC_INDICATION_TYPES.SITE_MSV2_DYNAMIC_COLUMNS_SORT_DISCLAIMER);
    }, [shouldShowSiteReportSortIndication, dispatch, removeIndication, selectedReport]);

    // Two years data limit indication for specific reports or Historical data not available indication (data available from November 2022 onward)
    useEffect(() => {
        if (!shouldStopUsingReportHistoricalData) {
            return;
        }
        // check report with 2 years data
        const twoYearsAgo = moment().subtract(2, 'years');
        const isBeforeTwoYearsAgo = moment(startDate).isBefore(twoYearsAgo.format('YYYY-MM-DD'));
        const shouldShowTwoYearsDataLimitIndication =
            REPORTS_WITH_TWO_YEARS_DATA.includes(selectedReport) && isBeforeTwoYearsAgo;

        if (shouldShowTwoYearsDataLimitIndication) {
            dispatch(addStaticIndication(twoYearsDataLimitIndication));
            return () => removeIndication(STATIC_INDICATION_TYPES.PARTIAL_REPORT_DATA);
        }
        // check filter with 2 years data
        const shouldShowFilterIndication =
            isFilterActive &&
            reportFilters &&
            reportFilters.length !== 0 &&
            reportFilters.some(({ type }) => UNAVAILABLE_MSV2_CREATIVE_REPORT_FILTERS.includes(type)) &&
            isBeforeTwoYearsAgo;

        if (shouldShowFilterIndication) {
            dispatch(addStaticIndication(reportFiltersDataUnavailableIndication));
            return () => removeIndication(STATIC_INDICATION_TYPES.PARTIAL_REPORT_DATA);
        }

        const shouldShowIndication =
            CHECK_DATA_NOT_AVAILABLE_REPORT_SET.has(selectedReport) &&
            moment(startDate).isBefore(HISTORICAL_DATA_AVAILABLE_FROM_DATE);
        if (!shouldShowIndication) {
            return;
        }
        dispatch(addStaticIndication(historicalDataNotAvailableIndication));
        return () => removeIndication(STATIC_INDICATION_TYPES.PARTIAL_REPORT_DATA);
    }, [
        dispatch,
        selectedReport,
        reportFilters,
        isFilterActive,
        startDate,
        removeIndication,
        shouldStopUsingReportHistoricalData,
    ]);

    // By Hour report we only have data the last 90 days.
    useEffect(() => {
        const ninetyDaysAgo = moment().subtract(90, 'days');
        const shouldShowIndication =
            selectedReport === HOUR_OF_DAY_REPORT_ID && moment(startDate).isBefore(ninetyDaysAgo.format('YYYY-MM-DD'));

        if (!shouldShowIndication) {
            return;
        }

        dispatch(addStaticIndication(byHourReportDataNotAvailableIndication));
        return () => removeIndication(STATIC_INDICATION_TYPES.PARTIAL_REPORT_DATA);
    }, [dispatch, selectedReport, startDate, removeIndication]);
};

export default useCampaignReportStaticIndications;
